import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import { CtaBand } from "@/components/CtaBand";
import { Markdown } from "@/components/Markdown";
import { SmartlaneButton } from "@/components/SmartlaneButton";
import { AiGeneratedBadge } from "@/components/AiGeneratedBadge";
import { articles, isAiGeneratedImage, publishedArticles } from "@/data/articles";
import { absoluteUrl } from "@/lib/site";
import { canonicalLink, hreflangLinks } from "@/lib/seo";

export const Route = createFileRoute("/aktuelles/news/$slug")({
  loader: ({ params }) => {
    const article = articles.find((a) => a.slug === params.slug);
    if (!article) throw notFound();
    return { article };
  },
  head: ({ params, loaderData }) => {
    const a = loaderData?.article;
    if (!a) return { meta: [{ title: "Artikel nicht gefunden | Smartlane" }] };
    const excerpt = a.content
      .replace(/[#*_>`]/g, "")
      .split("\n")
      .map((s) => s.trim())
      .find((s) => s.length > 40)
      ?.slice(0, 180) ?? a.title;
    const cover = a.heroImage ?? a.image ?? "";
    return {
      meta: [
        { title: `${a.title} | Smartlane News` },
        { name: "description", content: excerpt },
        { property: "og:title", content: a.title },
        { property: "og:description", content: excerpt },
        { property: "og:url", content: absoluteUrl(`/aktuelles/news/${params.slug}`) },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "article:published_time", content: a.date },
        ...(a.draft
          ? [
              { name: "robots", content: "noindex, nofollow" },
              { name: "googlebot", content: "noindex, nofollow" },
            ]
          : []),
      ],
      links: [canonicalLink(`/aktuelles/news/${params.slug}`), ...hreflangLinks(`/aktuelles/news/${params.slug}`)],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type":
              a.category === "presse"
                ? "NewsArticle"
                : (a.schemaType ?? "BlogPosting"),
            headline: a.title,
            description: excerpt,
            datePublished: a.date,
            dateModified: a.updated ?? a.date,
            inLanguage: "de-DE",
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": absoluteUrl(`/aktuelles/news/${params.slug}`),
            },
            about: (a.topics ?? [
              "Tourenplanung",
              "Disposition",
              "Logistik",
            ]).map((t) => ({ "@type": "Thing", name: t })),
            image: cover ? absoluteUrl(cover) : undefined,
            author: { "@type": "Organization", name: "Smartlane GmbH" },
            publisher: {
              "@type": "Organization",
              name: "Smartlane GmbH",
              logo: {
                "@type": "ImageObject",
                url: absoluteUrl("/favicon.svg"),
              },
            },
          }),
        },
      ],

    };
  },
  notFoundComponent: ArticleNotFound,
  component: ArticlePage,
});

const fmt = (d: string) =>
  new Date(d).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

function ArticleNotFound() {
  return (
    <section className="bg-white pt-40 pb-24">
      <div className="container-wide max-w-2xl text-center">
        <p className="eyebrow">404</p>
        <h1 className="text-display-lg mt-4 text-rich-black">Artikel nicht gefunden</h1>
        <p className="copy mt-4 text-muted-foreground">
          Dieser Beitrag existiert nicht oder wurde verschoben.
        </p>
        <Link
          to="/aktuelles/news"
          className="mt-8 inline-flex items-center gap-2 text-smartlane-orange font-body font-semibold"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
        </Link>
      </div>
    </section>
  );
}

function readingTime(content: string): number {
  const words = content
    .replace(/[#*_>`\[\]()!-]/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

function ArticlePage() {
  const { article } = Route.useLoaderData();
  const cover = article.heroImage ?? article.image;
  const minutes = readingTime(article.content);
  const related = publishedArticles
    .filter((a) => a.slug !== article.slug && new Date(a.date).getTime() < new Date(article.date).getTime())
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);

  return (
    <>
      <article>
        {/* Hero */}
        <section className="relative bg-rich-black text-white">
          {/* subtle dot grid + orange glow (clipped to the hero) */}
          <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-dot-grid-dark opacity-60" />
            <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-smartlane-orange/20 blur-[120px]" />
          </div>

          <div className={`container-wide relative pt-36 lg:pt-44 max-w-5xl ${cover ? "pb-48 lg:pb-64" : "pb-16 lg:pb-20"}`}>
            <Link
              to="/aktuelles/news"
              className="inline-flex items-center gap-2 text-white/60 hover:text-smartlane-orange font-body text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Zur Übersicht
            </Link>

            <div className="mt-8 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-smartlane-orange/40 bg-smartlane-orange/10 px-3 py-1 backdrop-blur-md">
                <span className="h-1.5 w-1.5 rounded-full bg-smartlane-orange animate-pulse" />
                <span className="font-body text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-smartlane-orange">
                  {article.draft ? "Entwurf" : article.category === "blog" ? "Blog" : "Presse"}
                </span>
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">
                {fmt(article.date)}
              </span>
            </div>

            <h1 className="font-heading mt-6 text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-wide text-white">
              {article.title}
            </h1>

            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/60 font-mono uppercase tracking-[0.18em]">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-smartlane-orange" />
                {fmt(article.date)}
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-white/40" />
                {minutes} Min. Lesezeit
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-white/40" />
                Smartlane Redaktion
              </span>
            </div>
          </div>

        </section>

        {/* Cover — overlaps hero and body for a smooth transition */}
        {cover && (
          <div className="container-wide relative z-10 max-w-6xl -mt-32 lg:-mt-48">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
              <img
                src={cover}
                alt={article.title}
                className={
                  article.coverFullFrame
                    ? "block w-full h-auto"
                    : "w-full h-auto object-cover aspect-[16/9]"
                }
              />
              {isAiGeneratedImage(cover) && <AiGeneratedBadge />}
            </div>
          </div>
        )}

        {/* Body */}
        <section className={`bg-white ${cover ? "-mt-32 lg:-mt-48 pt-48 lg:pt-64" : "pt-20"} pb-20 lg:pb-24`}>
          <div className="container-wide max-w-[880px]">
            <Markdown source={article.content} />

            <div className="mt-12 flex justify-center">
              <SmartlaneButton to="/kontakt" size="lg">
                Sprechen Sie uns an
              </SmartlaneButton>
            </div>

            {/* Footer meta */}
            <div className="mt-16 pt-8 border-t border-rich-black/10 flex flex-wrap items-center justify-between gap-4">
              <Link
                to="/aktuelles/news"
                className="inline-flex items-center gap-2 font-body text-sm font-semibold text-rich-black hover:text-smartlane-orange transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Alle Beiträge
              </Link>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {article.category === "blog" ? "Smartlane Blog" : "Presse"} · {fmt(article.date)}
              </span>
            </div>
          </div>
        </section>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-light-gray py-20">
          <div className="container-wide">
            <p className="eyebrow">Weiterlesen</p>
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {related.map((a) => {
                const img = a.image ?? a.heroImage;
                return (
                  <Link
                    key={a.slug}
                    to="/aktuelles/news/$slug"
                    params={{ slug: a.slug }}
                    className="group rounded-2xl bg-white border border-rich-black/5 overflow-hidden hover:shadow-lg hover:border-smartlane-orange/40 transition-all"
                  >
                    {img && (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={img}
                          alt={a.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        {fmt(a.date)}
                      </p>
                      <h3 className="font-heading text-lg uppercase tracking-wide text-rich-black mt-3 group-hover:text-smartlane-orange transition-colors line-clamp-3">
                        {a.title}
                      </h3>
                      <span className="mt-4 inline-flex items-center gap-2 font-body text-sm font-semibold text-smartlane-orange">
                        Weiterlesen<span className="sr-only">: {a.title}</span>{" "}
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <CtaBand
        title={
          <>
            Planen Sie Ihre Touren{" "}
            <span className="text-smartlane-orange">in Minuten</span>, nicht in Stunden.
          </>
        }
        description={
          <>
            Vereinbaren Sie noch heute eine Live-Demo mit unseren Experten und
            lassen Sie uns darüber sprechen, wie wir Ihre Disposition deutlich
            effizienter machen können.
          </>
        }
        primaryLabel="Live-Demo vereinbaren"
        secondaryLabel={undefined}
        secondaryTo={undefined}
      />
    </>
  );
}
